import type { AtmosphereRating } from "./atmosphere.ts";
import type { Face, Mood } from "./face.ts";
import type { CriterionId, CriterionResult, Judgement } from "./judge.ts";
import type { RequestDef } from "./requests.ts";
import { sayOf, type Tags } from "./tags.ts";
import type { TargetDef } from "./targets.ts";

/** What the delivery slip says about one finished piece. */
export interface SlipData {
  item: string;
  /** 1–5; null for a piece kneaded freely (nothing judged). */
  stars: number | null;
  /** Points for the customer's wish; null when there was no customer. */
  request: number | null;
  /** Its material and finish, said on a freely kneaded piece's slip. */
  appearance?: string;
  strokes: number;
  /** A word on it, from the customer or from the shop that looked it over. */
  comment: string;
  /** Who says the comment. */
  from: string;
  /** Their face beside their name, and how pleased it looks. */
  face: Face;
  mood: Mood;
  date: Date;
}

/** The shop the slip is from, which speaks for itself when the customer does not. */
const SHOP = "ねこ商会";
/** The shop's head clerk (番頭), who has the shop's word on the slip. */
export const SHOP_FACE: Face = {
  fur: "#a8826a",
  pattern: "tabby",
  marks: ["#7f5f4b"],
  wear: ["glasses"],
  backdrop: "#f6e9da",
};

/** Total score from which each star is earned (★1 is free). */
const STAR_FROM = [40, 70, 100, 150];

/** The shop's word when a criterion is the piece's best point. */
const PRAISE: Record<CriterionId, string> = {
  silhouette: "形がきれいで、よい品です",
  balance: "どっしり落ち着いた、よい品です",
  contour: "輪郭がきれいに出ていますね",
  likeness: "おてほんそっくりの見事な品です",
  symmetry: "バランスがよく、美しい品です",
  waist: "くびれが美しい、よい品です",
  hole: "穴の形がよく、使いやすそうです",
  ears: "二つのお耳がよく見える、愛らしい品です",
  efficiency: "手際のよさに感心しました",
  wish: "こちらの希望に寄せてくださって、うれしいです",
};

/**
 * The slip of a piece made for `request`. `air` is how strongly the air the letter hopes for
 * came across, if it was looked at (see atmosphere.ts).
 */
export function slipOf(
  j: Judgement,
  def: TargetDef,
  request: RequestDef,
  strokes: number,
  date: Date,
  air: AtmosphereRating | null,
): SlipData {
  const best = j.good[0];
  const stars = 1 + STAR_FROM.filter((min) => j.total >= min).length;
  // The clay keeps its holes whatever happens to it, so every piece is at least
  // topologically right, and the shop knows it.
  const topology = `位相的には申し分ない${def.item}です`;
  // A wish come true earns the customer's thanks before anything else; one come partway,
  // their word that they are glad of it, unless the shape is only topologically right. The
  // air, as far as it comes across, has a word of its own after that (or alone): with no word
  // on the wish and the air strong, that it is not what they pictured, but charms them.
  const [glad, thanks] = request.thanks;
  const wish = j.wishMet ? thanks : j.wishPleased && !j.topological ? glad : null;
  const felt = request.atmosphere && air && air.level > 0
    ? !wish && air.level === 2
      ? request.atmosphere.charmed
      : request.atmosphere.thanks[air.level - 1]
    : null;
  const customer = [wish, felt].filter((s) => s !== null).join("。") || null;
  const comment = customer ??
    (j.topological
      ? topology
      : best
      ? best.id === def.feature ? def.praise : PRAISE[best.id]
      : stars >= 3
      ? "ていねいな仕事ですね"
      : topology);
  return {
    item: request.item,
    stars,
    request: j.bonuses.find((c) => c.id === "wish")?.points ?? 0,
    strokes,
    comment,
    from: customer ? request.client : SHOP,
    // Glad of the piece, and overjoyed at their wish come true; the shop only looks it over.
    face: customer ? request.face : SHOP_FACE,
    mood: !customer ? "calm" : j.wishMet ? "delighted" : "glad",
    date,
  };
}

/**
 * The slip of a piece kneaded freely: no customer, nothing judged; its material and finish,
 * and the shop's word on how it comes across, if it was looked at (see tags.ts).
 */
export function freeSlipOf(
  strokes: number,
  appearance: string,
  date: Date,
  tags: Tags | null,
): SlipData {
  return {
    item: "自由作品",
    stars: null,
    request: null,
    appearance,
    strokes,
    comment: tags ? sayOf(tags) : "のびのびこねた、世界にひとつの品です",
    from: SHOP,
    face: SHOP_FACE,
    mood: "calm",
    date,
  };
}

// Slip layout, in pixels of the image.
const WIDTH = 1080, HEIGHT = 1500, MARGIN = 56;
const PHOTO_TOP = 206, PHOTO_GAP = 32, PHOTO_SIZE = (WIDTH - 2 * MARGIN - PHOTO_GAP) / 2;
const ITEM_Y = PHOTO_TOP + PHOTO_SIZE + 130;
const STAMP_X = WIDTH - MARGIN - 64, STAMP_Y = ITEM_Y - 26, STAMP_R = 58;
/** The face of who has a word on the piece, by their name at the foot, and the foot's middle. */
const FACE_SIZE = 80, FOOT_Y = HEIGHT - 76;

/** Stamp images are drawn this much of their width across (see stampImage). */
const STAMP_FILL = 0.84;
/** The box a stamp image covers on the slip, as fractions of the slip's width and height. */
export const STAMP_BOX = (() => {
  const side = (2 * STAMP_R) / STAMP_FILL;
  return {
    left: (STAMP_X - side / 2) / WIDTH,
    top: (STAMP_Y - side / 2) / HEIGHT,
    width: side / WIDTH,
  };
})();
const PAPER = "#fffaf3", INK = "#4a3530", INK_SOFT = "#8a6d62", RULE = "#e3cbb6";
const FRAME = "#f6e9da", STAR = "#e8a33d", STAMP = "#d9534f";

/**
 * Draws the slip with the two photos of the piece (front and back, both square) and the face
 * of who has a word on it (data.face in data.mood, loaded as an image; see faceImage); without
 * the stamp, it can be pressed on afterwards (see stampImage and STAMP_BOX).
 */
export function drawSlip(
  data: SlipData,
  photos: HTMLCanvasElement[],
  face: CanvasImageSource,
  stamped = true,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const g = canvas.getContext("2d")!;
  const font = getComputedStyle(document.body).fontFamily;
  const text = (s: string, x: number, y: number, size: number, color = INK, weight = 700) => {
    g.font = `${weight} ${size}px ${font}`;
    g.fillStyle = color;
    g.fillText(s, x, y);
  };
  const rule = (y: number, dash: number[] = []) => {
    g.strokeStyle = RULE;
    g.lineWidth = 3;
    g.setLineDash(dash);
    g.beginPath();
    g.moveTo(MARGIN, y);
    g.lineTo(WIDTH - MARGIN, y);
    g.stroke();
    g.setLineDash([]);
  };

  g.fillStyle = PAPER;
  g.fillRect(0, 0, WIDTH, HEIGHT);
  g.strokeStyle = RULE;
  g.lineWidth = 4;
  g.strokeRect(20, 20, WIDTH - 40, HEIGHT - 40);

  g.textAlign = "center";
  g.textBaseline = "alphabetic";
  text(`${SHOP}　納品票`, WIDTH / 2, 128, 60);
  rule(160);
  rule(170);

  // Photos, side by side on tinted frames.
  const size = PHOTO_SIZE, top = PHOTO_TOP;
  photos.forEach((photo, k) => {
    const x = MARGIN + k * (size + PHOTO_GAP);
    g.fillStyle = FRAME;
    g.beginPath();
    g.roundRect(x, top, size, size, 28);
    g.fill();
    g.drawImage(photo, x, top, size, size);
    g.textAlign = "left";
    text(k === 0 ? "まえ" : "うしろ", x + 22, top + size - 22, 28, INK_SOFT, 500);
  });

  g.textAlign = "left";
  let y = ITEM_Y;
  text(data.item, MARGIN, y, 72);
  // The shop's paw-print stamp, beside the item.
  if (stamped) drawStamp(g, STAMP_X, STAMP_Y, STAMP_R);
  y += 44;
  rule(y);

  // Receipt rows with dotted leaders.
  const rows: [string, string, string?][] = [];
  if (data.stars !== null) {
    rows.push(["評価", "★".repeat(data.stars) + "☆".repeat(5 - data.stars), STAR]);
  }
  if (data.request !== null) rows.push(["依頼達成", `+${data.request}`]);
  if (data.appearance) rows.push(["材質・仕上げ", data.appearance]);
  rows.push(["手数", `${data.strokes}こね`]);
  for (const [label, value, color] of rows) {
    y += 84;
    g.textAlign = "left";
    text(label, MARGIN, y, 44, INK_SOFT, 700);
    g.textAlign = "right";
    text(value, WIDTH - MARGIN, y, 48, color ?? INK);
    g.strokeStyle = RULE;
    g.lineWidth = 4;
    g.setLineDash([2, 12]);
    g.beginPath();
    g.moveTo(MARGIN + 230, y - 14);
    g.lineTo(WIDTH - MARGIN - 250, y - 14);
    g.stroke();
    g.setLineDash([]);
  }

  y += 72;
  rule(y, [14, 10]);
  // The comment, on up to three lines under a dashed rule, centred in the room between the
  // rule and the foot.
  g.textAlign = "center";
  const comment = fitComment(g, `「${data.comment}」`, WIDTH - 2 * MARGIN, font);
  const lead = comment.size * 1.4, middle = (y + 40 + FOOT_Y - FACE_SIZE / 2 - 14) / 2;
  const first = middle - lead * (comment.lines.length - 1) / 2 + comment.size * 0.35;
  comment.lines.forEach((line, k) => text(line, WIDTH / 2, first + k * lead, comment.size));

  // At the foot, who says it signs on the left (their face and name) and the date stands on
  // the right.
  g.drawImage(face, MARGIN, FOOT_Y - FACE_SIZE / 2, FACE_SIZE, FACE_SIZE);
  g.textBaseline = "middle";
  g.textAlign = "left";
  text(`${data.from}より`, MARGIN + FACE_SIZE + 16, FOOT_Y, 34, INK_SOFT);
  const d = data.date;
  const date = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${
    String(d.getDate()).padStart(2, "0")
  }`;
  g.textAlign = "right";
  text(date, WIDTH - MARGIN, FOOT_Y, 32, INK_SOFT, 500);
  g.textBaseline = "alphabetic";
  return canvas;
}

// Score card beside the slip in the photo, in pixels of the image.
const CARD_WIDTH = 760;
/** The judgement's rank colours, as on the result screen: gold, the accent, or mint. */
const RANK_COLORS: Record<string, [string, string]> = {
  SS: ["#f7c948", "#e8962e"],
  S: ["#f7c948", "#e8962e"],
  A: ["#e58a5c", "#e58a5c"],
};
const RANK_MINT: [string, string] = ["#5fb7a2", "#5fb7a2"];
const GAUGE_TRACK = "rgba(122, 86, 68, 0.12)";
const GAUGE_MAIN: [string, string] = ["#f3b38c", "#e58a5c"];
const GAUGE_BONUS: [string, string] = ["#f7d77a", "#eaa93a"];
const GAUGE_AIR: [string, string] = ["#c9b8ff", "#9a82e0"];
const ACCENT = "#e58a5c";

/** The air the letter hoped for and how strongly it came across, for the score card. */
export interface AirSeen {
  label: string;
  rating: AtmosphereRating;
}

/**
 * The commemorative photo: the stamped slip with a card beside it breaking the score
 * down as the result screen does (rank, total, and each criterion's gauge and points), and
 * the air hoped for if it was looked at.
 */
export function drawPhoto(
  slip: HTMLCanvasElement,
  j: Judgement,
  air: AirSeen | null,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH + CARD_WIDTH;
  canvas.height = HEIGHT;
  const g = canvas.getContext("2d")!;
  g.fillStyle = PAPER;
  g.fillRect(0, 0, canvas.width, HEIGHT);
  g.drawImage(slip, 0, 0);
  g.translate(WIDTH, 0);
  drawScoreCard(g, j, air);
  return canvas;
}

/** A row of the score card: a gauge, what it is and how full it stands, in words or points. */
interface Row {
  label: string;
  value: string;
  valueSize: number;
  /** After the value, as " / 30". */
  unit: string;
  fill: number;
}

function drawScoreCard(g: CanvasRenderingContext2D, j: Judgement, air: AirSeen | null): void {
  const W = CARD_WIDTH, left = MARGIN, right = W - MARGIN;
  const font = getComputedStyle(document.body).fontFamily;
  const text = (s: string, x: number, y: number, size: number, color = INK, weight = 700) => {
    g.font = `${weight} ${size}px ${font}`;
    g.fillStyle = color;
    g.fillText(s, x, y);
    return g.measureText(s).width;
  };
  const rule = (y: number) => {
    g.strokeStyle = RULE;
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(left, y);
    g.lineTo(right, y);
    g.stroke();
  };
  const gradient = (x0: number, x1: number, [from, to]: [string, string]) => {
    const fill = g.createLinearGradient(x0, 0, x1, 0);
    fill.addColorStop(0, from);
    fill.addColorStop(1, to);
    return fill;
  };

  g.strokeStyle = RULE;
  g.lineWidth = 4;
  g.strokeRect(20, 20, W - 40, HEIGHT - 40);
  g.textAlign = "center";
  g.textBaseline = "alphabetic";
  text("評価内訳", W / 2, 128, 60);
  rule(160);
  rule(170);

  // Rank badge, then the total, its split and the title.
  const r = 74, cx = left + r, cy = 306;
  g.fillStyle = gradient(cx - r, cx + r, RANK_COLORS[j.rank] ?? RANK_MINT);
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.fill();
  g.textBaseline = "middle";
  text(j.rank, cx, cy + 4, j.rank.length > 1 ? 64 : 80, "#fff");
  g.textBaseline = "alphabetic";
  g.textAlign = "left";
  const x = cx + r + 40;
  const w = text(String(j.total), x, 294, 92);
  text(" 点", x + w, 294, 40, INK_SOFT, 500);
  text(`基本 ${j.base} ＋ ボーナス ${j.bonus}`, x, 344, 30, INK_SOFT, 500);
  text(j.title, x, 392, 38, ACCENT);
  rule(438);

  // Criteria in two groups, as on the result screen but without the comments, and the air
  // hoped for apart from them.
  const points = (criteria: CriterionResult[], sign: string) =>
    criteria.map((c): Row => ({
      label: c.label,
      value: `${sign}${c.points}`,
      valueSize: 44,
      unit: ` / ${c.max}`,
      fill: c.points / c.max,
    }));
  const groups: [string, Row[], [string, string]][] = [
    ["基本（100点満点）", points(j.main, ""), GAUGE_MAIN],
    ["ボーナス", points(j.bonuses, "+"), GAUGE_BONUS],
  ];
  if (air) {
    const { label, rating } = air;
    groups.push([
      "雰囲気（点数には入りません）",
      [{ label, value: rating.text, valueSize: 36, unit: "", fill: rating.strength }],
      GAUGE_AIR,
    ]);
  }
  const rows = groups.reduce((n, [, r]) => n + r.length, 0), heading = 76;
  const pitch = Math.min(112, (HEIGHT - 60 - 438 - groups.length * heading) / rows);
  let y = 438;
  for (const [title, items, colors] of groups) {
    y += heading;
    g.textAlign = "left";
    text(title, left, y - 18, 30, INK_SOFT);
    for (const c of items) {
      y += pitch;
      const barY = y - 30;
      g.textAlign = "left";
      text(c.label, left, barY - 20, 40);
      g.textAlign = "right";
      const unitWidth = text(c.unit, right, barY - 20, 30, INK_SOFT, 500);
      text(c.value, right - unitWidth, barY - 20, c.valueSize);
      g.fillStyle = GAUGE_TRACK;
      g.beginPath();
      g.roundRect(left, barY, right - left, 16, 8);
      g.fill();
      const filled = Math.max(16, (right - left) * Math.min(1, c.fill));
      if (c.fill > 0) {
        g.fillStyle = gradient(left, left + filled, colors);
        g.beginPath();
        g.roundRect(left, barY, filled, 16, 8);
        g.fill();
      }
    }
  }
}

/** Font sizes a comment is tried at, largest first. */
const COMMENT_SIZES = [44, 42, 40, 38, 36, 34, 32];

/**
 * Breaks a comment into lines no wider than `width`: after punctuation marks, into at most
 * three lines, at the largest font size where that fits (in as few lines as will do, as even
 * as can be). A comment that cannot be broken so is cut into two lines wherever they fit.
 */
function fitComment(
  g: CanvasRenderingContext2D,
  comment: string,
  width: number,
  font: string,
): { lines: string[]; size: number } {
  for (const size of COMMENT_SIZES) {
    g.font = `700 ${size}px ${font}`;
    const lines = breakAfterPunctuation(g, comment, width);
    if (lines) return { lines, size };
  }
  for (const size of COMMENT_SIZES) {
    g.font = `700 ${size}px ${font}`;
    const fits = (s: string) => g.measureText(s).width <= width;
    let cut = comment.length - 1;
    while (cut > 1 && !fits(comment.slice(0, cut))) cut--;
    if (fits(comment.slice(cut))) {
      return { lines: [comment.slice(0, cut), comment.slice(cut)], size };
    }
  }
  return { lines: [comment], size: 30 };
}

/**
 * The comment on one line, or broken after punctuation marks into two or three no wider
 * than `width` in the current font (the fewest that fit, the widest of them as narrow as
 * can be); null when it cannot be.
 */
function breakAfterPunctuation(
  g: CanvasRenderingContext2D,
  comment: string,
  width: number,
): string[] | null {
  const breaks: number[] = [];
  for (let i = 1; i < comment.length - 1; i++) {
    if ("。、！？".includes(comment[i - 1])) breaks.push(i);
  }
  const ways = [
    [[]],
    breaks.map((i) => [i]),
    breaks.flatMap((i) => breaks.filter((j) => j > i).map((j) => [i, j])),
  ];
  for (const cutsOfEach of ways) {
    let best: string[] | null = null, widest = Infinity;
    for (const cuts of cutsOfEach) {
      const lines = [0, ...cuts].map((from, k) =>
        comment.slice(from, cuts[k] ?? comment.length).trimStart()
      );
      const w = Math.max(...lines.map((s) => g.measureText(s).width));
      if (w <= width && w < widest) {
        best = lines;
        widest = w;
      }
    }
    if (best) return best;
  }
  return null;
}

/** The stamp alone, on a transparent square `pixels` wide (with room for its rim). */
export function stampImage(pixels: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = pixels;
  drawStamp(canvas.getContext("2d")!, pixels / 2, pixels / 2, (pixels * STAMP_FILL) / 2);
  return canvas;
}

function drawStamp(g: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  g.save();
  g.translate(x, y);
  g.rotate(-0.2);
  g.globalAlpha = 0.85;
  g.strokeStyle = STAMP;
  g.fillStyle = STAMP;
  g.lineWidth = 6;
  g.beginPath();
  g.arc(0, 0, r, 0, Math.PI * 2);
  g.stroke();
  // Pad and four toes.
  g.beginPath();
  g.ellipse(0, r * 0.18, r * 0.34, r * 0.28, 0, 0, Math.PI * 2);
  g.fill();
  for (const [tx, ty] of [[-0.42, -0.2], [-0.15, -0.44], [0.15, -0.44], [0.42, -0.2]]) {
    g.beginPath();
    g.ellipse(tx * r, ty * r, r * 0.12, r * 0.15, 0, 0, Math.PI * 2);
    g.fill();
  }
  g.restore();
}
